/**
 * 预算草稿 → 请求（**纯逻辑层**，不依赖 React / DOM —— ㉛ 批确立的三层范式）
 *
 * 依据：G 域 PRD **§4.7**（三档建议）· **§4.9**（列表与操作）· `AC-G1.3` / `AC-G1.15` ~ `AC-G1.20`。
 *
 * ## 🔴 为什么必须有这一层
 *
 * 「新建预算」表单有 4 个字段 × 4 种周期（`event` 才要起止）× 3 档建议点选
 * ⇒ 判定分支不少。把它抽成纯函数有两条收益：
 * ① **断言无需渲染**（零依赖基座直接跑）；
 * ② 校验文案与**服务端 400 的 `message` 逐字相同** —— 靠**调用同一个内核**
 *    （`checkBudgetShape()`）实现，而不是在端侧再写一遍规则（㉛ 批的「对字」纪律）。
 *
 * ## 🔴 端侧零口径
 *
 * | 本文件**做** | 本文件**不做** |
 * |---|---|
 * | 草稿 → `CreateBudgetRequest`（含"元文本 → 分"用共享白名单 `yuanTextToCentsOrNull`） | ❌ 不算已用% / 不判档 / 不排序（都在服务端） |
 * | 调内核 `checkBudgetShape()` 给**同一句**错误文案 | ❌ 不自己写"金额必须大于 0"之类的第二份规则 |
 */

import {
  BudgetPeriod,
  BudgetSuggestionTier,
  BudgetTotalKey,
  centsToYuanText,
  checkBudgetShape,
  toCents,
  type BudgetCardView,
  type BudgetSuggestionView,
  type CategoryKind,
  type CategoryView,
  type Cents,
  type CreateBudgetRequest,
} from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/** 总预算的**展示用**选项 id（服务端用占位键，端侧不硬编那个保留字 —— 这里从共享层取） */
export const TOTAL_OPTION_ID = BudgetTotalKey;

export interface BudgetDraft {
  /** 分类 id；`''` = 未选。{@link TOTAL_OPTION_ID} = 总预算 */
  categoryId: string;
  period: BudgetPeriod;
  /** 金额（**元文本**，用户在输入框里打的） */
  amountText: string;
  /** `event` 必填（`YYYY-MM-DD`） */
  startDate: string;
  endDate: string;
}

/** 默认草稿：月度 + 空金额 + 未选分类 */
export function emptyBudgetDraft(period: BudgetPeriod = BudgetPeriod.Monthly): BudgetDraft {
  return { categoryId: '', period, amountText: '', startDate: '', endDate: '' };
}

/**
 * 本地校验 —— 🔴 **唯一判定处仍是内核**（`checkBudgetShape()`）。
 *
 * @param allCategories 账本全部分类（用来查所选分类的 `kind`；总预算占位键不需要）
 * @returns `null` = 通过；否则是**与 400 响应逐字相同**的中文文案
 */
export function budgetDraftIssue(
  draft: BudgetDraft,
  allCategories: readonly CategoryView[],
): string | null {
  if (draft.categoryId === '') return '请先选一个分类（或选「总预算」）。';

  const isTotal = draft.categoryId === TOTAL_OPTION_ID;
  const picked = isTotal ? undefined : allCategories.find((c) => c.id === draft.categoryId);
  if (!isTotal && picked === undefined) return '分类不存在或不属于当前账本。';

  const cents = yuanTextToCentsOrNull(draft.amountText);
  // ⚠️ 金额文本非数字时**先给金额的错误**（内核收的是"分"，喂 `NaN` 只会得到一句笼统文案）
  if (cents === null) return '金额请填数字（最多两位小数），例如 3000 或 3000.50。';

  return checkBudgetShape({
    period: draft.period,
    categoryId: draft.categoryId,
    categoryKind: isTotal ? null : ((picked?.kind ?? null) as CategoryKind | null),
    amount: cents,
    startDate: draft.period === BudgetPeriod.Event ? (draft.startDate === '' ? null : draft.startDate) : null,
    endDate: draft.period === BudgetPeriod.Event ? (draft.endDate === '' ? null : draft.endDate) : null,
  });
}

/** 草稿 → 请求体（**只在 {@link budgetDraftIssue} 通过时**返回非 `null`） */
export function budgetDraftToRequest(
  draft: BudgetDraft,
  allCategories: readonly CategoryView[],
): CreateBudgetRequest | null {
  if (budgetDraftIssue(draft, allCategories) !== null) return null;
  const cents = yuanTextToCentsOrNull(draft.amountText);
  if (cents === null) return null;

  const isEvent = draft.period === BudgetPeriod.Event;
  return {
    categoryId: draft.categoryId,
    period: draft.period,
    amount: cents as number,
    // 非事件周期**不带**起止（避免把上一次切周期时残留的日期发上去）
    startDate: isEvent ? draft.startDate : null,
    endDate: isEvent ? draft.endDate : null,
  };
}

/**
 * 采纳某一档建议（`AC-G1.20`：切分类 / 点某档 ⇒ 金额跟着变）。
 *
 * 🔴 **档值为 `null` ⇒ 原样返回**（该档"隐藏"，不是"填 0" —— `AC-G1.18`）。
 */
export function applySuggestion(
  draft: BudgetDraft,
  tier: BudgetSuggestionTier,
  suggestion: BudgetSuggestionView | undefined,
): BudgetDraft {
  if (suggestion === undefined) return draft;
  const cents = suggestion[tier];
  if (cents === null || cents === undefined) return draft;
  return { ...draft, amountText: centsToYuanTextForInput(cents as number) };
}

/**
 * 分 → 输入框文本（**不带千分位、不带币种符**）。
 *
 * 🔴 **必须走共享层白名单 `centsToYuanText()`** —— 端侧任何"分 ↔ 元"都不许自己 `／100`
 * （本项目 eslint 把 `/` / `Math.trunc` 全项目封死；㉝ 批就是顺手写了个 `/100` 被拦下）。
 * 这里只是把内核产物末尾的 `.00` 去掉，让输入框显示 `3000` 而不是 `3000.00`。
 */
function centsToYuanTextForInput(cents: number): string {
  const text = centsToYuanText(toCents(cents));
  return text.endsWith('.00') ? text.slice(0, -3) : text;
}

/**
 * 🆕 **从服务端卡片反序列化成草稿**（批次 `95` · 预算「调整」入口）。
 *
 * ## 🔴 为什么必须有这个函数
 *
 * `BudgetForm` 早就支持 `mode: 'edit'`，`PlanPage` 的 `save` 也早就分派了 `updateBudget` ——
 * 但**没有任何地方把 `mode: 'edit'` 传进去**（`setForm` 只在"新建"时被调用）。
 * ⇒ 用户能建预算、能启停，**改不了额度**（负责人 2026-10-22：「预算有，但是没有调整的入口」）。
 *
 * 这条正是「服务端落地 ≠ 承诺兑现」的同型：能力齐了，缺的是入口与反序列化。
 *
 * @param card 服务端下发的预算卡（`GET /api/budgets`）
 */
export function budgetDraftFromView(card: BudgetCardView): BudgetDraft {
  return {
    // 🔴 总预算在草稿里用占位键（与 `emptyBudgetDraft` 的取值域一致）
    categoryId: card.isTotal ? TOTAL_OPTION_ID : card.categoryId,
    period: card.period,
    // 金额走内核定点格式化（恒两位小数），端侧不写 `/100`
    amountText: centsToYuanText(toCents(card.amount as number)),
    startDate: card.startDate ?? '',
    endDate: card.endDate ?? '',
  };
}

// ═══════════════════════ 软提示（`AC-G1.28` 的两个方向 · 批 `109`） ═══════════════════════

/**
 * 软提示数据 —— 🔴 **两个方向的具体数字含义不同，所以带 `kind`**。
 *
 * | kind | 场景 | 屏幕上比的两个人 |
 * |---|---|---|
 * | `categoryOverflow` | 新增/改**分类**预算，使分类之和超过**已有**总预算 | `categorySumWithNew` vs `totalAmount` |
 * | `totalBelowSum` | 设/改**总预算**，新额度低于**已有**分类预算之和 | `categorySum` vs `totalAmount`（= 新额度） |
 *
 * ⚠️ 原实现只有第一个方向（且选总预算时直接 `return null`）⇒ 用户把总预算一路调低**没有任何提示**，
 * 而那一瞬间"总预算"这条线已经名不副实（负责人 2026-10-26：「分类总和不建议超过总预算」）。
 */
export interface BudgetSoftHint {
  kind: 'categoryOverflow' | 'totalBelowSum';
  /** 与总预算**同周期**的分类预算合计 */
  categorySum: Cents;
  /** 加上"本次要保存的这条"之后的合计（提示要给出**两个具体数字**） */
  categorySumWithNew: Cents;
  /** 被比较的总预算额度（`totalBelowSum` 时 = 用户刚输入的新额度） */
  totalAmount: Cents;
}

/** {@link softHintFor} 的输入 —— 全部是**页面已经拿到的值**，本函数不取数、不算口径 */
export interface BudgetSoftHintInput {
  /** 草稿选的分类 id（{@link TOTAL_OPTION_ID} = 总预算） */
  categoryId: string;
  period: BudgetPeriod;
  /** 用户输入的额度（元文本；无法解析 ⇒ 不提示） */
  amountText: string;
  /** 编辑态的预算 id（新建 ⇒ `null`；用于扣掉这条的旧额度） */
  editingId: string | null;
  /** 服务端下发的**同周期**分类预算合计 */
  categorySum: number;
  /** 现有总预算额度（没有总预算 ⇒ `null`） */
  totalAmount: number | null;
  /** 现有总预算的周期（没有 ⇒ `null`）—— 不同周期的额度相加没有意义 */
  totalPeriod: BudgetPeriod | null;
  /** 全部分类预算卡（仅编辑态用来扣旧值） */
  items: readonly BudgetCardView[];
}

/**
 * 软提示的**唯一判定处**（纯函数 · `AC-G1.28`）。
 *
 * 🔴 三条刻意：
 * ① 只在该预算**与总预算同周期**时比较（不同周期的额度相加没有意义）；
 * ② 编辑态要**先减掉这条预算的旧额度**，否则会重复计入自己；
 * ③ 一律**只提示不拦截** —— 返回值只喂给提示条（`issue === null` 就能保存）。
 */
export function softHintFor(input: BudgetSoftHintInput): BudgetSoftHint | null {
  const cents = yuanTextToCentsOrNull(input.amountText);
  if (cents === null) return null;

  // ── ② 设/改**总预算**：新额度低于分类预算之和 ⇒ 提示（比的是"用户刚输入的额度"） ──
  if (input.categoryId === TOTAL_OPTION_ID) {
    if (input.categorySum <= (cents as number)) return null;
    return {
      kind: 'totalBelowSum',
      categorySum: toCents(input.categorySum),
      // 这个方向没有"加上本次这条"的概念（总预算本身不进合计）⇒ 与 `categorySum` 同值
      categorySumWithNew: toCents(input.categorySum),
      totalAmount: toCents(cents as number),
    };
  }

  // ── ① 分类预算：与总预算**同周期**时才比较 ──
  if (input.totalAmount === null || input.totalPeriod === null) return null;
  if (input.period !== input.totalPeriod) return null;

  const oldCents =
    input.editingId === null
      ? 0
      : ((input.items.find((c) => c.id === input.editingId)?.amount ?? 0) as number);
  const withNew = input.categorySum - oldCents + (cents as number);
  if (withNew <= input.totalAmount) return null;

  return {
    kind: 'categoryOverflow',
    categorySum: toCents(input.categorySum),
    categorySumWithNew: toCents(withNew),
    totalAmount: toCents(input.totalAmount),
  };
}
